import { GridFilterModel } from "@mui/x-data-grid";
import { useCallback, useEffect, useRef, useState } from "react";
import { UseDataGridFiltersProps } from "../../Interfaces/CustomDataGrid.interfaces";
import { useTableStateUrl } from "./useTableStateUrl";

const DEFAULT_FILTER_MODEL: GridFilterModel = { items: [] };

export const useDataGridFilters = ({
  enableUrlParams = true,
  initialFilterModel = DEFAULT_FILTER_MODEL,
  onFilterChange
}: UseDataGridFiltersProps = {}) => {
  const [filterModel, setFilterModel] = useState<GridFilterModel>(DEFAULT_FILTER_MODEL);

  // Refs to prevent circular updates and track initialization
  const isInitializedRef = useRef(false);
  const isUpdatingFromUrlRef = useRef(false);

  // Shared URL state management
  const { getTableStateFromUrl, updateTableStateInUrl, clearTableStateFromUrl } = useTableStateUrl(enableUrlParams);

  const serializeFilterModel = useCallback((filterModel: GridFilterModel) => {
    const filterData = filterModel.items.map((item) => ({
      field: item.field,
      operator: item.operator,
      value: item.value
    }));

    return {
      ...((filterData.length > 0 && { items: filterData, logicOperator: filterModel.logicOperator }) || {
        items: undefined,
        logicOperator: undefined
      }),
      ...((filterModel.quickFilterValues &&
        filterModel.quickFilterValues.length > 0 && {
          quickFilterValues: filterModel.quickFilterValues,
          quickFilterLogicOperator: filterModel.quickFilterLogicOperator
        }) || { quickFilterValues: undefined, quickFilterLogicOperator: undefined })
    };
  }, []);

  const updateUrlParams = useCallback(
    (filterModel: GridFilterModel) => {
      const serializedFilters = serializeFilterModel(filterModel);
      updateTableStateInUrl(serializedFilters, isUpdatingFromUrlRef.current);
    },
    [serializeFilterModel, updateTableStateInUrl]
  );

  const handleFilterChange = useCallback(
    ({ items, logicOperator }: GridFilterModel) => {
      const { quickFilterValues, quickFilterLogicOperator } = (getTableStateFromUrl() as GridFilterModel) || {};
      const mergedFilterState = {
        items : items.map((item) => ({ ...item, value: item.value ?? "" })),
        logicOperator,
        quickFilterValues,
        quickFilterLogicOperator
      };

      setFilterModel(mergedFilterState);

      if (enableUrlParams && !isUpdatingFromUrlRef.current) {
        updateUrlParams(mergedFilterState);
      }

      onFilterChange?.(mergedFilterState);
    },
    [enableUrlParams, updateUrlParams, onFilterChange]
  );

  // Initialize filters from URL params on mount
  useEffect(() => {
    if (!enableUrlParams || isInitializedRef.current) return;

    const tableState = getTableStateFromUrl();

    if (tableState?.items || tableState?.quickFilterValues) {
      try {
        isUpdatingFromUrlRef.current = true; // ✅ Prevent circular updates

        setFilterModel({
          items: tableState.items || [],
          logicOperator: tableState.logicOperator,
          quickFilterValues: tableState.quickFilterValues,
          quickFilterLogicOperator: tableState.quickFilterLogicOperator
        });
      } catch (error) {
        console.warn("Failed to apply URL filter state:", error);
        clearTableStateFromUrl();
      } finally {
        isUpdatingFromUrlRef.current = false; // ✅ Reset the flag after update
      }
    } else if (initialFilterModel && !tableState) {
      setFilterModel(initialFilterModel);
    }

    isInitializedRef.current = true;
  }, [enableUrlParams, getTableStateFromUrl, clearTableStateFromUrl, initialFilterModel]);

  // Handle initial filter model changes after initialization
  useEffect(() => {
    if (!getTableStateFromUrl()) {
      setFilterModel(initialFilterModel);
    }
  }, [initialFilterModel, getTableStateFromUrl]);

  return {
    filterModel,
    handleFilterChange,
    updateUrlParams
  };
};
